/**
 * THE FROZEN RUN-SCOPED SALES OUTBOUND (`SPEC-APP.md` §11.6, `API-CONTRACT.md` §7).
 *
 * ONE read: `getKccRunSalesOutbound` at `{ path: { run_id } }` and NO query object at all — the
 * operation declares no query parameters.
 *
 * NOT A REGISTER. The payload is one unpaged object (`runId`, `snapshotId`, `cutoffAt`,
 * `publishedAt`, `rows`) with no `total`, `limit` or `offset`, so `RegisterConfig` cannot describe
 * it and the client doctrine's `limit=500` fetch-all and `items.length === total` assertion do not
 * apply (`SPEC-APP.md` §3.1). This is group 6's `useNetworkMap` / group 7's `useQueueStats` shape.
 *
 * Keyed under group 1 Phase 2's existing `kcc` domain, child `runOutbound`.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "../../../api/api";
import type { components } from "../../../api/client";
import { useBootstrap } from "../../../api/queries/useBootstrap";
import { queryKeys } from "../../../api/queryKeys";
import { classifyProblem, type ClassifiedProblem } from "../../../domain/problem";

export type RunSalesOutbound = components["schemas"]["RunSalesOutbound"];
export type RunSalesOutboundRow = components["schemas"]["RunSalesOutboundRow"];

export interface RunSalesOutboundResult {
  data: RunSalesOutbound | undefined;
  isLoading: boolean;
  problem: ClassifiedProblem | null;
  /** §11.6: a run with no KCC snapshot answers 404, and that is an empty state, not an error. */
  notRecorded: boolean;
  refetch: () => void;
}

export function useRunSalesOutbound(runId: string | null | undefined): RunSalesOutboundResult {
  const { data: boot } = useBootstrap();
  const tenantKey = boot?.tenant.key ?? null;
  const enabled = tenantKey !== null && Boolean(runId);

  const query = useQuery({
    queryKey: queryKeys.kcc.runOutbound(tenantKey ?? "_", runId ?? "_"),
    enabled,
    retry: false,
    queryFn: async () =>
      (await api.call("getKccRunSalesOutbound", {
        path: { run_id: runId as string },
      })) as RunSalesOutbound,
  });

  const problem = query.error ? classifyProblem(query.error) : null;
  const notRecorded = problem?.kind === "not_found";

  return {
    data: query.data,
    isLoading: enabled && query.isPending,
    problem: notRecorded ? null : problem,
    notRecorded,
    refetch: () => void query.refetch(),
  };
}
